import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Gift, Sparkles, MessageCircle, ExternalLink, Tag, Search, Briefcase, MapPin, Plus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  service: "Service",
  produit: "Produit",
  outil: "Outil",
  autre: "Autre",
};

const TYPE_COLORS = {
  service: "cbm-badge-type-propose",
  produit: "cbm-badge-type-cherche",
  outil: "cbm-badge-recommended",
  autre: "cbm-badge-verified",
};

function BenefitCard({ benefit, onContact, onViewProfile }) {
  const author = benefit.author || {};
  const initials = (author.nom_prenom || "M").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="cbm-card p-5 flex flex-col h-full relative overflow-hidden" data-testid={`benefit-${benefit._id}`}>
      {/* Exclusive badge top-right */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span className="cbm-badge cbm-badge-featured"><Sparkles size={9} className="mr-0.5" />Exclusif</span>
      </div>

      {/* Value display — hero */}
      <div className="mb-4 pr-20">
        <p className="cbm-label mb-1 flex items-center gap-1"><Tag size={10} /> Offre membre</p>
        <div
          className="inline-block px-4 py-2 rounded-lg font-outfit font-bold text-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(201,162,39,0.2), rgba(201,162,39,0.08))",
            border: "1px solid rgba(201,162,39,0.35)",
            color: "#DDC168",
            letterSpacing: "-0.02em",
          }}
          data-testid={`benefit-value-${benefit._id}`}
        >
          {benefit.value}
        </div>
      </div>

      {/* Title & description */}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`cbm-badge ${TYPE_COLORS[benefit.type] || "cbm-badge-verified"}`}>
            {TYPE_LABELS[benefit.type] || benefit.type}
          </span>
        </div>
        <h3
          className="font-outfit text-white mb-2 leading-snug"
          style={{ fontSize: "1.1875rem", fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          {benefit.title}
        </h3>
        <p
          className="line-clamp-3 mb-2"
          style={{ color: "#CFD9EC", fontSize: "0.9375rem", lineHeight: 1.55 }}
        >
          {benefit.description}
        </p>
        {benefit.conditions && (
          <p className="italic mt-2 border-l-2 border-gold/30 pl-2" style={{ color: "#AFC3E0", fontSize: "0.8125rem" }}>
            {benefit.conditions}
          </p>
        )}
      </div>

      {/* Author + CTA */}
      <div className="pt-4 mt-4 border-t cbm-divider flex items-center justify-between gap-3">
        <button
          data-testid={`view-benefit-author-${benefit._id}`}
          onClick={() => onViewProfile(author.id)}
          className="flex items-center gap-2.5 min-w-0 flex-1 text-left transition-colors hover:opacity-80"
          title="Voir profil"
        >
          {author.photo ? (
            <img src={`${API}${author.photo}`} alt={author.nom_prenom} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gold/20" />
          ) : (
            <div className="cbm-avatar w-9 h-9 text-sm flex-shrink-0">{initials}</div>
          )}
          <div className="min-w-0">
            <p className="text-white truncate" style={{ fontSize: "0.875rem", fontWeight: 600 }}>
              {author.nom_prenom || "Membre"}
            </p>
            {author.entreprise && (
              <p className="truncate" style={{ color: "#AFC3E0", fontSize: "0.8125rem" }}>
                {author.entreprise}
              </p>
            )}
          </div>
        </button>
        {author.whatsapp && (
          <button
            data-testid={`contact-benefit-${benefit._id}`}
            className="cbm-btn-primary text-sm py-2 px-3.5 flex-shrink-0"
            onClick={() => onContact(author)}
          >
            <MessageCircle size={14} /> Contacter
          </button>
        )}
      </div>
    </div>
  );
}

export default function Avantages() {
  const navigate = useNavigate();
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/benefits`, { withCredentials: true })
      .then(({ data }) => setBenefits(data))
      .finally(() => setLoading(false));
  }, []);

  const handleContact = (author) => {
    if (author.whatsapp) {
      const ph = author.whatsapp.replace(/\D/g, "");
      window.open(`https://wa.me/${ph}`, "_blank");
    } else if (author.email) {
      window.open(`mailto:${author.email}`);
    }
  };

  const handleViewProfile = (id) => {
    if (id) navigate(`/profil/${id}`);
  };

  const filtered = benefits.filter(b => {
    const matchSearch = !search || [b.title, b.description, b.value, b.author?.nom_prenom, b.author?.entreprise]
      .filter(Boolean).some(v => v.toLowerCase().includes(search.toLowerCase()));
    const matchType = !typeFilter || b.type === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
              <Gift size={18} className="text-gold" />
            </div>
            <h1 className="cbm-section-title">Avantages membres</h1>
          </div>
          <p className="text-cbm-muted text-sm ml-[52px]">Offres exclusives proposées par les membres du club</p>
        </div>
        <button
          data-testid="post-benefit-header-button"
          className="cbm-btn-primary text-sm"
          onClick={async () => {
            try {
              const { data } = await axios.get(`${API}/api/benefits/mine`, { withCredentials: true });
              if (data && (data.status === "pending" || data.status === "approved")) {
                navigate("/mon-profil#mon-avantage");
              } else {
                navigate("/avantages/new");
              }
            } catch {
              navigate("/avantages/new");
            }
          }}
        >
          <Plus size={15} /> Poster mon avantage
        </button>
      </div>

      {/* Filters */}
      <div className="cbm-filter-bar mb-6 flex items-center gap-3 flex-wrap animate-fade-in-up">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cbm-muted" />
          <input
            data-testid="benefits-search"
            className="cbm-input pl-9"
            placeholder="Rechercher un avantage, un membre, une offre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="benefits-type-filter"
          className="cbm-input"
          style={{ width: "auto", minWidth: 160 }}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Tous les types</option>
          <option value="service">Service</option>
          <option value="produit">Produit</option>
          <option value="outil">Outil</option>
          <option value="autre">Autre</option>
        </select>
        <span className="text-cbm-muted text-sm">
          {filtered.length} avantage{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="cbm-skeleton h-64 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="cbm-card p-12 text-center">
          <Gift size={40} className="text-cbm-muted mx-auto mb-3" />
          <h3 className="font-outfit font-semibold text-white text-base mb-1">Aucun avantage pour le moment</h3>
          <p className="text-cbm-muted text-sm mb-4">
            {search || typeFilter
              ? "Aucun avantage ne correspond à votre recherche."
              : "Soyez le premier à proposer un avantage exclusif aux membres !"}
          </p>
          {!search && !typeFilter && (
            <button
              data-testid="create-benefit-empty-cta"
              className="cbm-btn-primary text-sm"
              onClick={() => navigate("/avantages/new")}
            >
              Proposer mon avantage
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="benefits-grid">
          {filtered.map((b, idx) => (
            <div key={b._id} className={`animate-fade-in-up-delay-${Math.min(idx + 1, 6)}`}>
              <BenefitCard benefit={b} onContact={handleContact} onViewProfile={handleViewProfile} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
